import {
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query'
import { PaymentTerminalsApi } from '@trybeapp/sdk.shop'
import { useCurrentSiteId } from 'contexts/SiteConfig'
import { PaymentTerminal } from '@trybeapp/sdk'
import { usePageQueryParam } from 'hooks/UsePageQueryParam'

const api = new PaymentTerminalsApi()

export const usePaymentTerminals = (
  params: any = {},
  options: UseQueryOptions = {}
): UseQueryResult<any> => {
  const siteId = useCurrentSiteId()
  const [page] = usePageQueryParam()

  // With these defaults, data will always be refreshed
  // in the background if the cache is older than 6 hours.
  options.cacheTime = options.cacheTime ?? Infinity
  options.staleTime = options.staleTime ?? 6 * 60 * 60 * 1000 // 6 hours

  return useQuery(
    ['paymentTerminals', siteId, { page, ...params }],
    () => api.paymentTerminalsIndex(siteId, { page, ...params }),
    options
  )
}

type InfinitePaymentTerminalsData = Awaited<ReturnType<typeof fetchPaymentTerminals>>

export const useInfinitePaymentTerminals = (
  params: any = {},
  options?: UseInfiniteQueryOptions<InfinitePaymentTerminalsData>
) => {
  const siteId = useCurrentSiteId()

  return useInfiniteQuery<InfinitePaymentTerminalsData>(
    ['paymentTerminals', { ...params, siteId }],
    fetchPaymentTerminals,
    {
      getNextPageParam: (lastPage) => {
        return lastPage.nextPage
      },
      ...options,
    }
  )
}

const fetchPaymentTerminals = async ({ queryKey, pageParam: page = 1 }) => {
  const { siteId, ...params } = queryKey[1]

  const response = await api.paymentTerminalsIndex(siteId, { page, ...params })

  return {
    data: response.data as PaymentTerminal[],
    page,
    nextPage: response.meta.last_page > page ? page + 1 : undefined,
    total: response.meta.total,
  }
}

export const useReloadPaymentTerminals = () => {
  const queryClient = useQueryClient()
  const siteId = useCurrentSiteId()

  return useMutation(
    () => {
      return api.paymentTerminalsReload(siteId)
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['paymentTerminals'])
      },
    }
  )
}

export const useUpdatePaymentTerminal = () => {
  const queryClient = useQueryClient()
  const siteId = useCurrentSiteId()

  return useMutation(
    (args: any) => {
      const [paymentTerminalId, updateDetails] = args

      return api.paymentTerminalUpdate(siteId, paymentTerminalId, updateDetails)
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['paymentTerminals'])
      },
    }
  )
}
